import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import {
    IconChart,
    IconCalendar,
    IconFileText,
    IconChevronRight,
    IconCheck,
    IconX,
    IconPlus,
    IconBriefcase,
    LeafIcon,
    LoadingSpinner
} from '../components/ui/Icons';
import { API_URL } from '../utils/api';

const BillingAdmin = () => {
    const { fetchWithAuth } = useContext(AuthContext);
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [dateRange, setDateRange] = useState({
        from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        to: new Date().toISOString().split('T')[0]
    });

    const fetchBillingData = async () => {
        setLoading(true);
        try {
            const res = await fetchWithAuth(`${API_URL}/api/billing/admin/overview/?from=${dateRange.from}&to=${dateRange.to}`);
            if (res.ok) {
                const result = await res.json();
                setData(result);
            } else {
                showToast?.('Fehler beim Laden der Buchhaltungsdaten.', 'error');
            }
        } catch (err) {
            console.error(err);
            showToast?.('Verbindungsfehler.', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBillingData();
    }, [dateRange]);

    const handleExport = (format) => {
        const url = `${API_URL}/api/billing/admin/export/${format}/?from=${dateRange.from}&to=${dateRange.to}`;
        // For authenticated file downloads, we can't easily use fetch/blob for all browsers, 
        // but since we need the Auth header, we'll use an async approach to get a blob and then a local link.
        fetchWithAuth(url)
            .then(res => res.blob())
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `plantlix_buchhaltung_${dateRange.from}_${dateRange.to}.${format === 'excel' ? 'xlsx' : 'ods'}`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            })
            .catch(err => {
                console.error(err);
                showToast?.('Export fehlgeschlagen.', 'error');
            });
    };

    if (loading && !data) return <LoadingSpinner />;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#0f172a] pb-12">
            {/* Header */}
            <div className="bg-white dark:bg-[#1e293b] border-b border-gray-200 dark:border-white/5 sticky top-0 z-10 shadow-sm">
                <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-plantlix-500 rounded-xl flex items-center justify-center text-white shadow-lg shadow-plantlix-500/20">
                            <IconChart className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-none">Billing & Admin</h1>
                            <p className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest mt-1">Buchhaltung & Finanzen</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 bg-gray-100 dark:bg-white/5 p-1 rounded-xl">
                            <input 
                                type="date" 
                                value={dateRange.from}
                                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                                className="bg-transparent border-none text-xs font-bold text-gray-700 dark:text-gray-300 focus:ring-0 cursor-pointer"
                            />
                            <span className="text-gray-400">→</span>
                            <input 
                                type="date" 
                                value={dateRange.to}
                                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                                className="bg-transparent border-none text-xs font-bold text-gray-700 dark:text-gray-300 focus:ring-0 cursor-pointer"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 mt-8">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    {[
                        { label: 'Einnahmen (Bezahlt)', value: `${data?.summary?.total_paid_eur || 0}€`, color: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-500/10' },
                        { label: 'Ausstehend', value: `${data?.summary?.total_pending_eur || 0}€`, color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-500/10' },
                        { label: 'Fehlgeschlagen', value: `${data?.summary?.total_failed_eur || 0}€`, color: 'text-rose-500', bg: 'bg-rose-50 dark:bg-rose-500/10' },
                        { label: 'Transaktionen', value: data?.summary?.total_transactions || 0, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-500/10' }
                    ].map((stat, i) => (
                        <div key={i} className={`p-6 rounded-3xl border border-white/10 shadow-sm ${stat.bg}`}>
                            <p className="text-xs font-black uppercase text-gray-500 dark:text-gray-400 tracking-widest mb-1">{stat.label}</p>
                            <p className={`text-3xl font-black ${stat.color}`}>{stat.value}</p>
                        </div>
                    ))}
                </div>

                {/* Export Actions */}
                <div className="bg-white dark:bg-[#1e293b] rounded-3xl p-6 border border-gray-100 dark:border-white/5 shadow-sm mb-8 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div>
                        <h2 className="text-xl font-black text-gray-900 dark:text-white mb-1">Export für Buchhaltung</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Alle Transaktionen im gewählten Zeitraum als Excel oder ODF herunterladen.</p>
                    </div>
                    <div className="flex gap-4">
                        <button 
                            onClick={() => handleExport('excel')}
                            className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl text-sm font-black transition-all shadow-lg shadow-emerald-600/20 active:scale-95"
                        >
                            <IconFileText className="w-4 h-4" /> Excel (.xlsx)
                        </button>
                        <button 
                            onClick={() => handleExport('ods')}
                            className="flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-2xl text-sm font-black transition-all shadow-lg shadow-slate-800/20 active:scale-95"
                        >
                            <IconFileText className="w-4 h-4" /> ODF (.ods)
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Transactions Table */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-white dark:bg-[#1e293b] rounded-3xl overflow-hidden border border-gray-100 dark:border-white/5 shadow-sm">
                            <div className="p-6 border-b border-gray-100 dark:border-white/5 flex items-center justify-between">
                                <h3 className="font-black text-gray-900 dark:text-white">Transaktionen</h3>
                                <span className="px-3 py-1 bg-gray-100 dark:bg-white/5 rounded-full text-[10px] font-black text-gray-500 uppercase">Live</span>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="bg-gray-50 dark:bg-white/5 text-[10px] font-black uppercase text-gray-400 tracking-widest">
                                            <th className="px-6 py-4">Datum</th>
                                            <th className="px-6 py-4">Kunde</th>
                                            <th className="px-6 py-4">Betrag</th>
                                            <th className="px-6 py-4 text-right">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                                        {data?.transactions?.map((tx, i) => (
                                            <tr key={i} className="hover:bg-gray-50 dark:hover:bg-white/5 transition-colors group">
                                                <td className="px-6 py-4">
                                                    <div className="text-sm font-bold text-gray-900 dark:text-white">{tx.Datum.split(' ')[0]}</div>
                                                    <div className="text-[10px] text-gray-400 font-bold">{tx.Datum.split(' ')[1]}</div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm font-bold text-gray-900 dark:text-white">{tx.Kunde || 'Gast'}</div>
                                                    <div className="text-[10px] text-gray-400 font-bold truncate max-w-[150px]">{tx.Beschreibung}</div>
                                                </td>
                                                <td className="px-6 py-4 text-sm font-black text-gray-900 dark:text-white">
                                                    {tx['Betrag (EUR)']}€
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-black uppercase ${
                                                        tx.Status === 'Bezahlt' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400' :
                                                        tx.Status === 'Ausstehend' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400' :
                                                        'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400'
                                                    }`}>
                                                        {tx.Status === 'Bezahlt' ? <IconCheck className="w-3 h-3" /> : tx.Status === 'Ausstehend' ? '⏳' : <IconX className="w-3 h-3" />}
                                                        {tx.Status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                        {(!data?.transactions || data.transactions.length === 0) && (
                                            <tr>
                                                <td colSpan="4" className="px-6 py-20 text-center text-gray-400 font-bold italic">Keine Transaktionen gefunden.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Subscriptions & Sidebar */}
                    <div className="space-y-6">
                        <div className="bg-white dark:bg-[#1e293b] rounded-3xl p-6 border border-gray-100 dark:border-white/5 shadow-sm">
                            <h3 className="font-black text-gray-900 dark:text-white mb-4">Aktive Abonnements</h3>
                            <div className="space-y-4">
                                {data?.subscriptions?.map((sub, i) => (
                                    <div key={i} className="p-4 bg-gray-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/5">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
                                                sub.price_id.includes('gold') ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-700'
                                            }`}>
                                                {sub.price_id.includes('gold') ? '🥇 GOLD' : '🥈 SILBER'}
                                            </span>
                                            <span className="text-[10px] text-emerald-500 font-black uppercase">Aktiv</span>
                                        </div>
                                        <p className="text-sm font-black text-gray-900 dark:text-white truncate">{sub.sponsor}</p>
                                        <div className="flex items-center justify-between mt-3 text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">
                                            <span>Nächste Abrechnung</span>
                                            <span className="text-gray-900 dark:text-white">{sub.current_period_end}</span>
                                        </div>
                                    </div>
                                ))}
                                {(!data?.subscriptions || data.subscriptions.length === 0) && (
                                    <p className="text-xs text-gray-400 italic text-center py-4">Keine aktiven Abonnements.</p>
                                )}
                            </div>
                        </div>

                        {/* Quick Tips */}
                        <div className="p-6 bg-gradient-to-br from-plantlix-500 to-emerald-600 rounded-3xl text-white shadow-xl shadow-plantlix-500/20">
                            <LeafIcon className="w-8 h-8 mb-4 opacity-50" />
                            <h3 className="text-lg font-black mb-2">Steuer-Tipp</h3>
                            <p className="text-xs text-white/80 leading-relaxed font-bold">
                                Transaktionen werden automatisch nach dem Buchungsdatum sortiert. Nutze den Excel-Export für DATEV oder deinen Steuerberater.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BillingAdmin;
